import { useEffect } from "react";

import { toast } from "sonner";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { useAuth } from "../../../context/AuthContext";
import { useData } from "../../../context/DataContext";
import { cn, getCharCount } from "../../../lib/utils.js";

const ActivityDialog = ({ open, onOpenChange, editingActivityId, newActivity, setNewActivity, onSubmit }) => {
  const { user } = useAuth();
  const { globalProjectId } = useData();

  const isDescriptionOverLimit = getCharCount(newActivity.description) > 500;

  useEffect(() => {
    if (open && !editingActivityId) {
      setNewActivity((prev) => ({
        ...prev,
        projectId: prev.projectId || (globalProjectId && globalProjectId !== "ALL" ? globalProjectId : ""),
        date: prev.date || new Date().toISOString().split("T")[0],
        assignedTo: prev.assignedTo || user?.id || ""
      }));
    }
  }, [open, editingActivityId, globalProjectId, user, setNewActivity]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }

    const requiredFields = [
      { key: "projectId", label: "Project" },
      { key: "date", label: "Date" },
      { key: "title", label: "Activity Title" },
      { key: "description", label: "Description" },
      { key: "assignedTo", label: "Assigned To" }
    ];

    const missingFields = requiredFields.filter((f) => !newActivity[f.key]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.map((f) => f.label).join(", ")}`);
      return;
    }

    onSubmit(e);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{editingActivityId ? "Edit Activity" : "Log New Activity"}</DialogTitle>
            <DialogDescription>
              {editingActivityId ? "Update activity information" : "Record a daily construction activity"}
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="projectId">
                  Project <span className="text-red-500">*</span>
                </Label>
                <LazySearchSelect
                  value={newActivity.projectId}
                  onChange={(value) => setNewActivity({ ...newActivity, projectId: value })}
                  placeholder="Select project"
                  searchPlaceholder="Search projects..."
                  apiUrl="/projects"
                  dataKey="items"
                  labelKey="name"
                  valueKey="id"
                />
              </div>
              <div>
                <Label htmlFor="date">
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={newActivity.date}
                  onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="title">
                Activity Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={newActivity.title}
                onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                placeholder="e.g., Concrete pouring for foundation"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={newActivity.description}
                onChange={(e) =>
                  setNewActivity({
                    ...newActivity,
                    description: e.target.value
                  })
                }
                className={cn(isDescriptionOverLimit && "border-red-500")}
                rows={3}
                required
              />
              <WordCount text={newActivity.description} />
            </div>

            <div>
              <Label htmlFor="assignedTo">
                Assigned To <span className="text-red-500">*</span>
              </Label>
              <LazySearchSelect
                value={newActivity.assignedTo}
                onChange={(value) => setNewActivity({ ...newActivity, assignedTo: value })}
                placeholder="Select worker"
                searchPlaceholder="Search workers..."
                apiUrl="/users"
                dataKey="items"
                labelKey="name"
                valueKey="id"
              />
            </div>
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button
              type="submit"
              variant="primary"
              className="w-full py-6 rounded-lg shadow-lg"
              disabled={isDescriptionOverLimit}
            >
              {editingActivityId ? "Update Activity" : "Log Activity"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
